import { ContactWizardStep } from './contact-wizard-steps';
import { PhotoLabel } from './photo-label';
import { IdScanFieldSources } from '../id-scan/id-scan-fields';

// =============================================================================
// Add Contact wizard draft.
//
// WHY THIS EXISTS. "Take photo" on Android hands the page to the camera app.
// Chrome is then in the background, and Android may kill its renderer to make
// room (it does this on a 12 GB S24 Ultra too; free RAM is not the test, the
// background process priority is). When the camera returns, Chrome reloads the
// tab, shows its own "Unable to complete previous operation due to low memory"
// toast and drops the photo. Everything typed into the wizard went with it.
//
// So the wizard keeps an encrypted snapshot of itself. On reload the notebook
// page finds it and reopens the wizard where the user was.
//
// WHAT IS KEPT. Role, step, the fields the user TYPED, the blank note the
// photos were uploaded to, and the names of the photos the server already has.
// Photo BYTES are never kept here: uploaded photos are listed back from the
// server, and a photo that had not finished uploading is reported as lost so
// the user retakes it (unless the server turns out to have it after all).
// Nothing is invented.
//
// WHAT IS NEVER KEPT. Anything read from the ID (barcode or OCR), while it is
// still marked as scanned. Licence data stays in memory only, by decision
// (2026-11-01): a scanned field is left out of the draft, and on restore the
// ID photo already on the server is read again. A scanned field the user then
// edits is the user's own entry from that point and is kept like any other.
//
// AT REST it is AES-GCM ciphertext under the client KEK (PGPEncryptionService),
// the same rule as note drafts: with no KEK nothing is stored, because this is
// witness and suspect PII.
// =============================================================================

export const CONTACT_DRAFT_KEY_PREFIX = 'fn_contact_draft_v1_';
export const CONTACT_DRAFT_VERSION = 1;
export const CONTACT_DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface ContactDraftPhoto {
  readonly name: string;
  readonly label: PhotoLabel;
  readonly sizeBytes: number;
}

/** A photo that had not finished uploading when the draft was written. Name may be '' (no label yet). */
export interface ContactDraftPendingPhoto {
  readonly name: string;
  readonly sizeBytes: number;
}

export interface ContactDraftPerson {
  readonly [field: string]: string | number | null;
}

export interface ContactDraft {
  readonly version: number;
  readonly savedAtIso: string;
  readonly userId: string;
  readonly notebookID: string;
  readonly noteID: string;
  readonly associatedNoteID: string;
  readonly addUpdate: string;
  readonly step: ContactWizardStep;
  readonly person: ContactDraftPerson;
  /** True when scanned fields were left out, so the restore can say so. */
  readonly scannedFieldsOmitted: boolean;
  readonly uploadedPhotos: readonly ContactDraftPhoto[];
  readonly pendingPhotos: readonly ContactDraftPendingPhoto[];
  /**
   * Set when the note was saved but the CaseManagement Person row was not.
   * The restore reopens on review with Retry, which re-sends only the Person
   * save for this note ID. Scanned fields are still left out, as above.
   */
  readonly personPendingNoteId?: string;
}

/** Person fields worth keeping. Derived and UI-only members are left out. */
export const CONTACT_DRAFT_PERSON_FIELDS: readonly string[] = [
  'id', 'identification', 'firstName', 'lastName', 'dobYear', 'dobMonth', 'dobDay', 'estimatedAge',
  'streetName', 'streetNumber', 'city', 'state', 'country', 'zipCode',
  'cellPhone', 'cellPhoneCountryCode', 'homePhone', 'homePhoneCountryCode', 'email', 'notes', 'personType',
];

export function contactDraftKey(userId: string, notebookID: string): string {
  return CONTACT_DRAFT_KEY_PREFIX + userId + '_' + notebookID;
}

export interface KeyedDraftStorage {
  readonly length: number;
  key(index: number): string | null;
  removeItem(key: string): void;
}

/** Remove every contact draft on this device, for any user. Called at logout. */
export function clearAllContactDrafts(storage: KeyedDraftStorage): number {
  const keys: string[] = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(CONTACT_DRAFT_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    return 0;
  }
  return keys.length;
}

/** Typed person fields only: any field a scan filled (per `scanSources`) is left out. */
export function pickPersonFields(person: object, scanSources: IdScanFieldSources): ContactDraftPerson {
  const source = person as Record<string, unknown>;
  const scanned = new Set<string>(Object.keys(scanSources).filter((key) => !!(scanSources as Record<string, unknown>)[key]));
  const picked: Record<string, string | number | null> = {};
  for (const field of CONTACT_DRAFT_PERSON_FIELDS) {
    if (scanned.has(field)) {
      continue;
    }
    const value = source[field];
    if (typeof value === 'string' || typeof value === 'number') {
      picked[field] = value;
    } else if (value === null) {
      picked[field] = null;
    }
  }
  return picked;
}

/** True once there is something the user would be sorry to lose. */
export function isWorthKeeping(draft: ContactDraft): boolean {
  return !!draft.person['personType'] || draft.uploadedPhotos.length > 0;
}

export function isDraftFresh(draft: ContactDraft, now: Date): boolean {
  const saved = Date.parse(draft.savedAtIso);
  if (isNaN(saved)) {
    return false;
  }
  const age = now.getTime() - saved;
  return age >= 0 && age <= CONTACT_DRAFT_MAX_AGE_MS;
}

function isStep(value: unknown): value is ContactWizardStep {
  return typeof value === 'number' && Object.values(ContactWizardStep).includes(value);
}

function isPendingList(value: unknown): value is ContactDraftPendingPhoto[] {
  return Array.isArray(value) && value.every((photo) =>
    !!photo && typeof photo === 'object'
    && typeof (photo as ContactDraftPendingPhoto).name === 'string'
    && typeof (photo as ContactDraftPendingPhoto).sizeBytes === 'number');
}

/** Rebuild the person from the known field list only, so no other key reaches the model. */
function whitelistPerson(value: object): ContactDraftPerson {
  const source = value as Record<string, unknown>;
  const person: Record<string, string | number | null> = {};
  for (const field of CONTACT_DRAFT_PERSON_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(source, field)) {
      continue;
    }
    const v = source[field];
    if (typeof v === 'string' || typeof v === 'number' || v === null) {
      person[field] = v;
    }
  }
  return person;
}

function isPhotoList(value: unknown): value is ContactDraftPhoto[] {
  return Array.isArray(value) && value.every((photo) =>
    !!photo && typeof photo === 'object'
    && typeof (photo as ContactDraftPhoto).name === 'string'
    && typeof (photo as ContactDraftPhoto).label === 'string'
    && typeof (photo as ContactDraftPhoto).sizeBytes === 'number');
}

/** Parse decrypted JSON. Anything malformed, from another version or another user is refused. */
export function parseContactDraft(json: string, userId: string, notebookID: string): ContactDraft | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const d = parsed as Record<string, unknown>;
  if (
    d['version'] !== CONTACT_DRAFT_VERSION
    || d['userId'] !== userId
    || d['notebookID'] !== notebookID
    || typeof d['savedAtIso'] !== 'string'
    || typeof d['noteID'] !== 'string'
    || typeof d['associatedNoteID'] !== 'string'
    || typeof d['addUpdate'] !== 'string'
    || !isStep(d['step'])
    || !d['person'] || typeof d['person'] !== 'object'
    || typeof d['scannedFieldsOmitted'] !== 'boolean'
    || !isPhotoList(d['uploadedPhotos'])
    || !isPendingList(d['pendingPhotos'])
    || (d['personPendingNoteId'] !== undefined && (typeof d['personPendingNoteId'] !== 'string' || d['personPendingNoteId'] !== d['noteID']))
  ) {
    return null;
  }
  return { ...(parsed as ContactDraft), person: whitelistPerson(d['person'] as object) };
}

export interface ContactDraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ContactDraftCrypto {
  kek(): string | null;
  encrypt(plain: string, kek: string): Promise<string | undefined>;
  decrypt(cipher: string, kek: string): Promise<string | undefined>;
}

/**
 * Storage plus encryption, with both injected so the spec runs without a
 * browser KEK. Every method swallows storage errors: a draft is a safety net,
 * and a full or blocked localStorage must never break the wizard itself.
 */
export class ContactDraftStore {
  constructor(private readonly storage: ContactDraftStorage, private readonly crypto: ContactDraftCrypto) { }

  /** Returns true when an encrypted copy was written. */
  async save(draft: ContactDraft): Promise<boolean> {
    const key = contactDraftKey(draft.userId, draft.notebookID);
    if (!isWorthKeeping(draft)) {
      this.clear(draft.userId, draft.notebookID);
      return false;
    }
    const kek = this.crypto.kek();
    if (!kek) {
      return false;
    }
    try {
      const cipher = await this.crypto.encrypt(JSON.stringify(draft), kek);
      if (!cipher) {
        return false;
      }
      this.storage.setItem(key, cipher);
      return true;
    } catch {
      return false;
    }
  }

  async load(userId: string, notebookID: string, now: Date): Promise<ContactDraft | null> {
    const key = contactDraftKey(userId, notebookID);
    let cipher: string | null;
    try {
      cipher = this.storage.getItem(key);
    } catch {
      return null;
    }
    if (!cipher) {
      return null;
    }
    const kek = this.crypto.kek();
    if (!kek) {
      return null;
    }
    let plain: string | undefined;
    try {
      plain = await this.crypto.decrypt(cipher, kek);
    } catch {
      plain = undefined;
    }
    const draft = plain ? parseContactDraft(plain, userId, notebookID) : null;
    if (!draft || !isDraftFresh(draft, now)) {
      this.clear(userId, notebookID);
      return null;
    }
    return draft;
  }

  clear(userId: string, notebookID: string): void {
    try {
      this.storage.removeItem(contactDraftKey(userId, notebookID));
    } catch {
      // A blocked store has nothing to clear.
    }
  }
}
